import { useMemo } from 'react';
import { usePref } from '@taki/ui';
import type { ShellAppEntry } from '../types.js';

/**
 * [MENU DEV] Masquage local des apps : la cle de pref, sa resolution, son hook.
 *
 * BUT : pendant une demo client, ne montrer que les 2 ou 3 apps pertinentes.
 * Ce n'est PAS une desinstallation : rien n'est supprime, la preference vit
 * dans le localStorage de CE poste, et "Tout afficher" la remet a zero.
 *
 * REGLE QUI TIENT TOUT LE LOT : seules les surfaces qui AFFICHENT une liste
 * d'apps passent par `useVisibleAppEntries` (dock, menu Home, barre d'onglets
 * du hub Parametres). Celles qui RESOLVENT ou qui ECRIVENT gardent la liste
 * complete, et il y en a deux :
 *   - la resolution de route (TakiShell) : filtrer ferait tomber l'app ouverte
 *     sur "App introuvable" au moment meme ou on la masque depuis l'overlay
 *     pose au-dessus d'elle ;
 *   - le panneau Dock du hub Parametres : il REECRIT les prefs `dock.apps` et
 *     `dock.shortcuts` a partir de la liste recue, donc une liste filtree
 *     effacerait l'epinglage des apps masquees. Le masquage cache, il ne
 *     detruit jamais une autre preference.
 *
 * CE MASQUAGE EST COSMETIQUE, EN AUCUN CAS UN CONTROLE D'ACCES. Une app
 * masquee reste montable par son URL, par l'historique et par pickApp : c'est
 * voulu. La vraie securite, ce sont les Security Rules (AGENTS.md regle 6) ;
 * l'interface ne fait que masquer.
 *
 * POURQUOI CE MODULE VIT DANS dev/ ET MEURT AVEC LE MENU DEV : l'ecran DEV est
 * la SEULE interface capable de defaire un masquage. Un module permanent
 * laisserait un jour une preference active sans aucun ecran pour la remettre a
 * zero, c'est-a-dire un shell vide sans porte de sortie.
 *
 * Suppression : voir packages/shell/src/dev/README.md.
 */

/**
 * Cle de pref, convention <domaine>.<sujet> du repo (dock.apps,
 * app.<id>.filters). Le domaine n'est PAS le dock : le masquage porte aussi le
 * menu Home et les onglets du hub Parametres.
 */
export const HIDDEN_APPS_PREF_KEY = 'apps.hidden';

/**
 * Ensemble des ids masques, seule lecture autorisee de la pref.
 *
 * Le garde teste la FORME, pas seulement la nullite : la pref vient du
 * localStorage, donc d'un JSON qui a pu etre edite a la main. Sur une valeur ni
 * nulle ni tableau (`{}`, `123`), un `new Set(...)` direct jetterait "not
 * iterable" depuis le composant racine du routeur, c'est-a-dire un shell
 * entierement blanc, sans aucune interface pour defaire la pref. Une pref
 * illisible se comporte donc comme une pref absente.
 */
export function hiddenIdSet(hidden: readonly string[] | null): Set<string> {
  return new Set<string>(Array.isArray(hidden) ? hidden : []);
}

/**
 * Liste des apps a afficher, la pref etant la liste des ids MASQUES.
 *
 * ON STOCKE LES DEVIATIONS, JAMAIS LES VISIBLES (convention deja documentee
 * dans le repo : "cle absente = visible"). Trois consequences voulues : une app
 * nouvellement installee est visible par defaut, une pref vide ou corrompue
 * donne un shell NORMAL, et l'id d'une app desinstallee est simplement ignore,
 * sans purge.
 *
 * Pref absente, vide ou illisible : on rend `entries` TEL QUEL (meme
 * reference), pour ne pas invalider les useMemo des appelants a chaque rendu.
 */
export function resolveVisibleApps(
  hidden: readonly string[] | null,
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  const hiddenSet = hiddenIdSet(hidden);
  if (hiddenSet.size === 0) return entries;
  return entries.filter((entry) => !hiddenSet.has(entry.id));
}

/**
 * Bascule le masquage d'une app et rend la nouvelle pref.
 *
 * Ne purge JAMAIS les ids inconnus : une app desinstallee puis reinstallee
 * retrouve son etat, et une pref ecrite depuis un autre jeu d'apps n'est pas
 * silencieusement amputee.
 *
 * Meme garde de forme que {@link hiddenIdSet} : une pref corrompue repart d'une
 * liste vide plutot que de faire tomber la case a cocher.
 */
export function toggleAppHidden(hidden: readonly string[] | null, id: string): string[] {
  const current: readonly string[] = Array.isArray(hidden) ? hidden : [];
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

/**
 * Le hook que consomment les surfaces d'affichage.
 *
 * `import.meta.env.DEV` est ecrit ICI, LITTERALEMENT, et nulle part ailleurs
 * pour le masquage : une seule source de verite. NE JAMAIS le remplacer par un
 * helper isDev(), une variable intermediaire ou un optional chaining : c'est
 * cette forme textuelle exacte que Vite remplace par une constante au build, et
 * c'est ce remplacement qui permet a esbuild de supprimer la branche. Un build
 * de production ne peut donc pas heriter d'un localStorage qui masque des apps
 * sans offrir le menu DEV pour le defaire.
 *
 * Honnetement : en production le corps se reduit a "return entries" plus une
 * lecture localStorage au montage (usePref est appele sans condition, regle des
 * hooks). Zero interface, zero appel reseau, zero comportement, mais ce n'est
 * pas litteralement zero octet.
 */
export function useVisibleAppEntries(
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  const [hidden] = usePref<string[] | null>(HIDDEN_APPS_PREF_KEY, null);
  return useMemo(
    () => (import.meta.env.DEV ? resolveVisibleApps(hidden, entries) : entries),
    [hidden, entries],
  );
}
